import { useMemo, useState } from 'react';
import { Report } from '@mui/icons-material';
import { Button, CircularProgress, Container, Stack, Tooltip, Typography } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowSelectionModel,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from '@mui/x-data-grid';
import { User } from '@shared';

type TypeProps = {
  users: User[];
  isSkipExistingUsers: boolean;
  isLoading: boolean;
  onCancel: () => void;
  onBack: () => void;
  onSendInvites: (data: User[]) => void;
};

const columns: GridColDef[] = [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    flex: 0.4,
    renderHeader: () => <Typography variant='subtitle2'>Send invite</Typography>,
  },
  {
    field: 'uniqueUser',
    headerName: 'Unique user',
    flex: 0.45,
    sortable: false,
    editable: false,
    renderCell: ({ row }) => (
      <Stack direction='row' alignItems='center' gap={2}>
        <Typography variant='tableCell'>{row.existingUser ? 'No' : 'Yes'}</Typography>
        {row.existingUser && (
          <Tooltip title='Duplicated contact' placement='top' arrow>
            <Report />
          </Tooltip>
        )}
      </Stack>
    ),
  },
  {
    field: 'firstName',
    headerName: 'First name',
    flex: 0.45,
    editable: false,
    sortable: false,
    hideable: true,
  },
  {
    field: 'lastName',
    headerName: 'Last name',
    flex: 0.45,
    sortable: false,
    editable: false,
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 1,
    editable: false,
    sortable: false,
  },
];

export const InvitesUsers = ({
  users,
  isSkipExistingUsers,
  onCancel,
  onBack,
  onSendInvites,
  isLoading,
}: TypeProps) => {
  const [invitedUsers, setInvitedUsers] = useState<GridRowSelectionModel>(() =>
    users.filter((u) => !isSkipExistingUsers || !u.existingUser).map((r) => r.uuid || '')
  );

  const labelSubmitButton = useMemo(() => {
    return invitedUsers.length ? 'Add & Send' : 'Add';
  }, [invitedUsers]);

  const handleSubmit = () => {
    const data = users.map((item) => ({
      ...item,
      sendEmailInvitation: invitedUsers.includes(item.uuid ?? ''),
    }));
    onSendInvites(data);
  };

  return (
    <Container maxWidth='sm'>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
        <Stack>
          <Typography variant='h5' fontWeight={400}>
            Add Users
          </Typography>
          <Typography variant='body2'>Users to add: {users.length}</Typography>
          <Typography variant='body2'>Users to send invite: {invitedUsers.length}</Typography>
        </Stack>
        <Stack direction='row' spacing={1}>
          <Button variant='outlined' disabled={isLoading} onClick={onCancel}>
            Cancel
          </Button>
          <Button variant='outlined' disabled={isLoading} onClick={onBack}>
            Back
          </Button>
          <Button variant='contained' disabled={!users.length || isLoading} onClick={handleSubmit}>
            {isLoading ? <CircularProgress size={18} color='inherit' /> : labelSubmitButton}
          </Button>
        </Stack>
      </Stack>
      <DataGrid
        sx={{ '.MuiDataGrid-overlayWrapper': { height: '52px' } }}
        getRowId={(row) => row.uuid}
        rows={users}
        columns={columns}
        loading={isLoading}
        checkboxSelection
        hideFooter
        rowSelectionModel={invitedUsers}
        onRowSelectionModelChange={setInvitedUsers}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
      />
    </Container>
  );
};
